import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@trillion/ui/components/ui/button";
import {
  Card,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@trillion/ui/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@trillion/ui/components/ui/dialog";
import { Input } from "@trillion/ui/components/ui/input";
import { Label } from "@trillion/ui/components/ui/label";
import { Textarea } from "@trillion/ui/components/ui/textarea";
import { toast } from "sonner";
import { api } from "../lib/api";
import type { Team } from "../types/board";

export function TeamsPage() {
  const [teams, setTeams] = useState<Team[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isOpen, setIsOpen] = useState(false);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [isCreating, setIsCreating] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    loadTeams();
  }, []);

  function loadTeams() {
    api<{ teams: Team[] }>("/api/teams")
      .then((res) => setTeams(res.teams))
      .catch(() => {
        setTeams([]);
        toast.error("Failed to load teams");
      })
      .finally(() => setIsLoading(false));
  }

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim()) return;
    setIsCreating(true);
    try {
      const team = await api<Team>("/api/teams", {
        method: "POST",
        body: JSON.stringify({ name: name.trim(), description: description.trim() || null }),
      });
      setTeams((prev) => [...prev, team]);
      setName("");
      setDescription("");
      setIsOpen(false);
      toast.success("Team created");
    } catch {
      toast.error("Failed to create team");
    } finally {
      setIsCreating(false);
    }
  }

  return (
    <div className="p-6">
      <div className="mb-6 flex items-center justify-between">
        <h1 className="text-2xl font-semibold">Teams</h1>
        <Dialog open={isOpen} onOpenChange={setIsOpen}>
          <DialogTrigger asChild>
            <Button>Create Team</Button>
          </DialogTrigger>
          <DialogContent>
            <form onSubmit={handleCreate}>
              <DialogHeader>
                <DialogTitle>Create Team</DialogTitle>
                <DialogDescription>
                  Create a new team to collaborate on diagrams.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="team-name">Name</Label>
                  <Input
                    id="team-name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Engineering"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="team-description">Description</Label>
                  <Textarea
                    id="team-description"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Optional team description"
                    rows={3}
                  />
                </div>
              </div>
              <DialogFooter>
                <Button type="submit" disabled={isCreating}>
                  {isCreating ? "Creating..." : "Create"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {isLoading ? (
        <p className="text-sm text-muted-foreground">Loading teams...</p>
      ) : teams.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-lg border border-dashed p-12">
          <p className="mb-4 text-sm text-muted-foreground">
            You are not a member of any teams yet.
          </p>
          <Button onClick={() => setIsOpen(true)}>Create Team</Button>
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {teams.map((team) => (
            <Card
              key={team.team_id}
              className="cursor-pointer transition-colors hover:bg-accent/50"
              onClick={() => navigate(`/teams/${team.team_id}`)}
            >
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base">{team.name}</CardTitle>
                </div>
                <CardDescription>
                  {team.member_count ?? 0} member{(team.member_count ?? 0) !== 1 ? "s" : ""}
                  {team.description ? ` — ${team.description}` : ""}
                </CardDescription>
              </CardHeader>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
